import { LIMITS, type Attachment, type Channel, type Member, type Message } from '@spawnwire/shared';
import { actions, useStore, type UiMessage } from '../store/store';
import { ui } from '../store/ui';
import { errorMessage, http } from './api';

const PAGE = LIMITS.messagePageSize;
const inflight = new Map<string, Promise<void>>();

function once(key: string, fn: () => Promise<void>): Promise<void> {
  const existing = inflight.get(key);
  if (existing) return existing;
  const p = fn().finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

export function loadLatest(channelId: string): Promise<void> {
  return once(`latest:${channelId}`, async () => {
    actions.setMessagesLoading(channelId, true);
    try {
      const list = await http.get<Message[]>(`/channels/${channelId}/messages?limit=${PAGE}`);
      actions.mergeMessages(channelId, list, 'latest', PAGE);
    } catch (err) {
      actions.setMessagesLoading(channelId, false);
      throw err;
    }
  });
}

export function loadOlder(channelId: string): Promise<void> {
  const state = useStore.getState().messages[channelId];
  if (!state || !state.hasMoreBefore || state.loadingBefore) return Promise.resolve();
  const oldest = state.ids.find((id) => !id.startsWith('pending:'));
  if (!oldest) return Promise.resolve();
  return once(`before:${channelId}`, async () => {
    actions.setMessagesLoading(channelId, true);
    try {
      const list = await http.get<Message[]>(`/channels/${channelId}/messages?before=${oldest}&limit=${PAGE}`);
      actions.mergeMessages(channelId, list, 'before', PAGE);
    } catch (err) {
      actions.setMessagesLoading(channelId, false);
      ui.toast(errorMessage(err), 'error');
    }
  });
}

export function loadNewer(channelId: string): Promise<void> {
  const state = useStore.getState().messages[channelId];
  const newest = state?.ids.filter((id) => !id.startsWith('pending:')).at(-1);
  if (!state || state.atLatest || !newest) return Promise.resolve();
  return once(`after:${channelId}`, async () => {
    const list = await http.get<Message[]>(`/channels/${channelId}/messages?after=${newest}&limit=${PAGE}`);
    actions.mergeMessages(channelId, list, 'after', PAGE);
  });
}

export async function loadAround(channelId: string, messageId: string): Promise<void> {
  const list = await http.get<Message[]>(`/channels/${channelId}/messages?around=${messageId}&limit=${PAGE}`);
  actions.mergeMessages(channelId, list, 'around', PAGE);
}

/**
 * After a reconnect, fetch whatever arrived while we were away for every
 * channel we have history for. Large gaps reset the channel to its latest page.
 */
export async function resyncLoadedChannels(): Promise<void> {
  const { messages } = useStore.getState();
  await Promise.all(
    Object.entries(messages).map(async ([channelId, state]) => {
      if (!state.loaded || !state.atLatest) return;
      const newest = state.ids.filter((id) => !id.startsWith('pending:')).at(-1);
      try {
        if (!newest) return await loadLatest(channelId);
        const list = await http.get<Message[]>(`/channels/${channelId}/messages?after=${newest}&limit=${LIMITS.maxMessagePageSize}`);
        if (list.length >= LIMITS.maxMessagePageSize) await loadLatest(channelId);
        else actions.mergeMessages(channelId, list, 'after', LIMITS.maxMessagePageSize);
      } catch {
        /* the channel may have been deleted or hidden while offline */
      }
    }),
  );
}

export async function loadMembers(spaceId: string, force = false): Promise<void> {
  if (!force && useStore.getState().membersLoaded[spaceId]) return;
  return once(`members:${spaceId}`, async () => {
    const list = await http.get<Member[]>(`/spaces/${spaceId}/members`);
    actions.setMembers(spaceId, list);
  });
}

export function refreshMembersIfLoaded(spaceId: string): void {
  if (useStore.getState().members[spaceId]) void loadMembers(spaceId, true).catch(() => {});
}

// ---- Sending ---------------------------------------------------------------------

export interface OutgoingAttachment {
  attachment: Attachment;
}

let nonceCounter = 0;
function newNonce(): string {
  return `${Date.now().toString(36)}-${(++nonceCounter).toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Sends a message with an optimistic placeholder that becomes the real message when the server answers. */
export async function sendMessage(channelId: string, content: string, opts: { replyToId?: string; attachments?: Attachment[] } = {}): Promise<boolean> {
  const state = useStore.getState();
  const me = state.me;
  if (!me) return false;
  const channel = state.channels[channelId];
  const nonce = newNonce();
  const replyTarget = opts.replyToId ? state.messages[channelId]?.byId[opts.replyToId] : undefined;
  const placeholder: UiMessage = {
    id: `pending:${nonce}`,
    channelId,
    spaceId: channel?.spaceId ?? null,
    author: me,
    content,
    type: opts.replyToId ? 'reply' : 'default',
    createdAt: new Date().toISOString(),
    editedAt: null,
    replyTo: replyTarget
      ? { id: replyTarget.id, authorId: replyTarget.author?.id ?? null, author: replyTarget.author, content: replyTarget.content, hasAttachments: replyTarget.attachments.length > 0, deleted: false }
      : null,
    attachments: opts.attachments ?? [],
    reactions: [],
    mentions: [],
    mentionRoles: [],
    mentionEveryone: false,
    pinned: false,
    embeds: [],
    thread: null,
    publishedAt: null,
    nonce,
  };
  actions.addPending(placeholder, nonce);
  try {
    const message = await http.post<Message>(`/channels/${channelId}/messages`, {
      content,
      nonce,
      replyToId: opts.replyToId,
      attachmentIds: opts.attachments?.map((a) => a.id),
    });
    actions.upsertMessage({ ...message, nonce });
    return true;
  } catch (err) {
    actions.failPending(channelId, nonce, errorMessage(err));
    return false;
  }
}

export async function retryPending(channelId: string, pendingId: string): Promise<void> {
  const msg = useStore.getState().messages[channelId]?.byId[pendingId];
  if (!msg) return;
  actions.dropPending(channelId, pendingId.slice('pending:'.length));
  await sendMessage(channelId, msg.content, { replyToId: msg.replyTo?.id, attachments: msg.attachments });
}

// ---- Read state ------------------------------------------------------------------

const lastAck = new Map<string, string>();

/** Marks a channel read up to its newest message (debounced per channel). */
export function ackChannel(channel: Channel | undefined): void {
  if (!channel?.lastMessageId) return;
  const state = useStore.getState();
  const rs = state.readStates[channel.id];
  if (rs && rs.lastReadMessageId && rs.lastReadMessageId >= channel.lastMessageId && rs.mentionCount === 0) return;
  if (lastAck.get(channel.id) === channel.lastMessageId) return;
  lastAck.set(channel.id, channel.lastMessageId);
  actions.setReadState({ channelId: channel.id, lastReadMessageId: channel.lastMessageId, mentionCount: 0 });
  void http.post(`/channels/${channel.id}/ack`, { messageId: channel.lastMessageId }).catch(() => lastAck.delete(channel.id));
}

export function markSpaceRead(spaceId: string): void {
  const state = useStore.getState();
  for (const c of Object.values(state.channels)) if (c.spaceId === spaceId && c.type !== 'category' && c.type !== 'voice' && c.type !== 'stage') ackChannel(c);
}

// ---- Reactions ---------------------------------------------------------------------

export async function toggleReaction(message: Message, emoji: string): Promise<void> {
  const mine = message.reactions.find((r) => r.emoji === emoji)?.me ?? false;
  actions.toggleReactionLocal(message.channelId, message.id, emoji, !mine);
  const path = `/channels/${message.channelId}/messages/${message.id}/reactions/${encodeURIComponent(emoji)}`;
  try {
    if (mine) await http.del(path);
    else await http.put(path);
  } catch (err) {
    actions.toggleReactionLocal(message.channelId, message.id, emoji, mine);
    ui.toast(errorMessage(err), 'error');
  }
}

export async function openDm(userId: string): Promise<Channel | null> {
  try {
    const channel = await http.post<Channel>('/users/@me/channels', { recipientId: userId });
    actions.upsertChannel(channel);
    return channel;
  } catch (err) {
    ui.toast(errorMessage(err), 'error');
    return null;
  }
}

/** Runs a mutation and shows any error as a toast. Returns undefined on failure. */
export async function attempt<T>(fn: () => Promise<T>, success?: string): Promise<T | undefined> {
  try {
    const result = await fn();
    if (success) ui.toast(success, 'success');
    return result;
  } catch (err) {
    ui.toast(errorMessage(err), 'error');
    return undefined;
  }
}
